import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Select } from "antd";
import { useSelector } from "react-redux";

import { User } from "../../services/api";
import { notification } from "../../services";
import {
  selectCategories,
  selectModules
} from "../../containers/App/selectors";
import {
  categoriesInterface,
  modulesInterface
} from "../../typings/interfaces";
import styles from "./dominantCategoriesModal.scss";

interface Props {
  userId?: string;
  initialDominantCategories?: { module?: string; category?: string }[];
  buttonClassName?: string;
}

const DominantCategoriesModal = ({
  userId,
  initialDominantCategories,
  buttonClassName
}: Props) => {
  const modules = useSelector(selectModules) || [];
  const categories = useSelector(selectCategories) || [];

  const first = (initialDominantCategories || [])[0] || {};
  const [isOpen, setIsOpen] = useState(false);
  const [moduleName, setModuleName] = useState(first.module || undefined);
  const [categoryName, setCategoryName] = useState(first.category || undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const next = (initialDominantCategories || [])[0] || {};
    setModuleName(next.module || undefined);
    setCategoryName(next.category || undefined);
  }, [JSON.stringify(initialDominantCategories || [])]);

  const filteredCategories = useMemo(() => {
    if (!moduleName) return [];
    return categories.filter((c: categoriesInterface) => c.moduleName === moduleName);
  }, [categories, moduleName]);

  if (!userId) return null;

  const handleModuleChange = (value: string) => {
    setModuleName(value);
    setCategoryName(undefined);
  };

  const handleSave = () => {
    if (!moduleName || !categoryName) {
      notification.showError("Odaberite modul i kategoriju.");
      return;
    }

    setIsSaving(true);
    User.assignDominantCategories(
      userId,
      [{ module: moduleName, category: categoryName }],
      () => {
        setIsSaving(false);
        notification.showError("Greška pri spremanju dominantne kategorije.");
      }
    )
      .then(() => {
        setIsSaving(false);
        setIsOpen(false);
        notification.show("Dominantna kategorija je spremljena.");
      })
      .catch(() => {
        setIsSaving(false);
      });
  };

  const handleClear = () => {
    setIsSaving(true);
    User.assignDominantCategories(userId, [], () => {
      setIsSaving(false);
      notification.showError("Greška pri uklanjanju dominantne kategorije.");
    })
      .then(() => {
        setIsSaving(false);
        setModuleName(undefined);
        setCategoryName(undefined);
        setIsOpen(false);
        notification.show("Dominantna kategorija je uklonjena.");
      })
      .catch(() => {
        setIsSaving(false);
      });
  };

  const handleCancel = () => {
    const current = (initialDominantCategories || [])[0] || {};
    setModuleName(current.module || undefined);
    setCategoryName(current.category || undefined);
    setIsOpen(false);
  };

  return (
    <React.Fragment>
      <Button
        className={buttonClassName}
        type="default"
        onClick={() => setIsOpen(true)}
      >
        Dom. kategorija
      </Button>
      <Modal
        title="Dominantna kategorija"
        visible={isOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="clear" onClick={handleClear} disabled={isSaving}>
            Ukloni
          </Button>,
          <Button key="cancel" onClick={handleCancel} disabled={isSaving}>
            Odustani
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={!moduleName || !categoryName}
          >
            Spremi
          </Button>
        ]}
      >
        <div className={styles.modalBody}>
          <div className={styles.field}>
            <label className={styles.label}>Modul</label>
            <Select
              className={styles.select}
              placeholder="Odaberite modul"
              value={moduleName}
              onChange={handleModuleChange}
              allowClear
              getPopupContainer={(trigger: any) => trigger.parentNode}
            >
              {modules.map((m: modulesInterface) => (
                <Select.Option key={m.name} value={m.name}>
                  {m.displayName || m.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Kategorija</label>
            <Select
              className={styles.select}
              placeholder="Odaberite kategoriju"
              value={categoryName}
              onChange={setCategoryName}
              disabled={!moduleName}
              allowClear
              getPopupContainer={(trigger: any) => trigger.parentNode}
            >
              {filteredCategories.map((c: categoriesInterface) => (
                <Select.Option key={c.name} value={c.name}>
                  {c.displayName || c.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default DominantCategoriesModal;



// WEBPACK FOOTER //
// ./src/components/DominantCategoriesModal/DominantCategoriesModal.tsx